<script setup>
// Nexus IA → Avisos de manutenção (custom/app/controllers/api/v1/accounts/neuxs/manutencao_controller.rb).
// O servidor reserva e envia em segundo plano; a tela acompanha pelo estado de
// cada linha e se atualiza sozinha enquanto houver envio em andamento.
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createNexusApi, erroDaApi } from './api';
import { montaAviso } from './aviso';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const api = computed(() => createNexusApi(props.accountId));

const ESTADOS = [
  'aguardando',
  'enviando',
  'enviado',
  'fora_da_janela',
  'falhou',
  'cancelado',
];
const SELECIONAVEIS = ['aguardando', 'falhou', 'fora_da_janela'];
// As classes ficam no .vue: o Tailwind não lê os .js desta pasta.
const COR = {
  aguardando: 'bg-n-amber-3 text-n-amber-11',
  enviando: 'bg-n-blue-3 text-n-blue-11',
  enviado: 'bg-n-teal-3 text-n-teal-11',
  fora_da_janela: 'bg-n-slate-3 text-n-slate-11',
  falhou: 'bg-n-ruby-3 text-n-ruby-11',
  cancelado: 'bg-n-slate-3 text-n-slate-10',
};

const carregando = ref(true);
const erro = ref('');
const filtro = ref('');
const dados = ref({ mensagem: {}, resumo: {}, contatos: [] });
const texto = ref('');
const textoCarregado = ref(false);
const salvando = ref(false);
const agindo = ref('');
const selecionados = ref(new Set());
let relogio = null;

const aguardando = computed(() => dados.value.resumo.aguardando || 0);
// Só quem já teve a manutenção encerrada no SGP pode receber o aviso.
const liberados = computed(() => dados.value.liberados ?? aguardando.value);
const esperando = computed(() => aguardando.value - liberados.value);
const selecionavel = contato =>
  SELECIONAVEIS.includes(contato.estado) && contato.liberado !== false;
const enviando = computed(() =>
  dados.value.contatos.some(c => c.estado === 'enviando')
);
const previa = computed(() => montaAviso(texto.value, 'Bruna Silva'));
const mudouTexto = computed(
  () => texto.value.trim() !== (dados.value.mensagem.texto || '')
);
const semMensagem = computed(() => !dados.value.mensagem.texto);

const quando = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';
const janelaAberta = iso => Boolean(iso) && new Date(iso) > new Date();

const carrega = async ({ silencioso = false } = {}) => {
  if (!silencioso) carregando.value = true;
  try {
    const { data } = await api.value.manutencao(filtro.value);
    dados.value = data;
    if (!textoCarregado.value) {
      texto.value = data.mensagem.texto || '';
      textoCarregado.value = true;
    }
    selecionados.value = new Set(
      [...selecionados.value].filter(id =>
        data.contatos.some(c => c.id === id && selecionavel(c))
      )
    );
    erro.value = '';
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_NEXUS.MANUTENCAO.ERRO'));
  } finally {
    carregando.value = false;
  }
};

// Enquanto houver envio em andamento, relê a cada 4s.
watch(enviando, emAndamento => {
  clearInterval(relogio);
  if (emAndamento) {
    relogio = setInterval(() => carrega({ silencioso: true }), 4000);
  }
});
watch([() => props.accountId, filtro], () => carrega(), { immediate: true });
onBeforeUnmount(() => clearInterval(relogio));

const alterna = id => {
  const novo = new Set(selecionados.value);
  if (novo.has(id)) novo.delete(id);
  else novo.add(id);
  selecionados.value = novo;
};

const salva = async () => {
  salvando.value = true;
  try {
    const { data } = await api.value.salvaMensagem(texto.value);
    dados.value = { ...dados.value, mensagem: data.mensagem };
    useAlert(t('NEUXS_NEXUS.MANUTENCAO.SALVA'));
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.MANUTENCAO.ERRO_ACAO')));
  } finally {
    salvando.value = false;
  }
};

const envia = async ids => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('NEUXS_NEXUS.MANUTENCAO.CONFIRMA_ENVIO'))) return;
  agindo.value = ids ? 'selecionados' : 'todos';
  try {
    const { data } = await api.value.enviaAvisos(ids);
    const partes = [
      data.reservados
        ? t('NEUXS_NEXUS.MANUTENCAO.RESERVADOS', { n: data.reservados })
        : t('NEUXS_NEXUS.MANUTENCAO.NENHUM_RESERVADO'),
    ];
    if (data.bloqueados) {
      partes.push(
        t('NEUXS_NEXUS.MANUTENCAO.BLOQUEADOS', { n: data.bloqueados })
      );
    }
    useAlert(partes.join(' '));
    selecionados.value = new Set();
    await carrega({ silencioso: true });
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.MANUTENCAO.ERRO_ACAO')));
  } finally {
    agindo.value = '';
  }
};

const remove = async () => {
  const ids = [...selecionados.value];
  const pergunta = t('NEUXS_NEXUS.MANUTENCAO.CONFIRMA_REMOVER', {
    n: ids.length,
  });
  // eslint-disable-next-line no-alert
  if (!window.confirm(pergunta)) return;
  agindo.value = 'remover';
  try {
    const { data } = await api.value.removeAvisos(ids);
    useAlert(t('NEUXS_NEXUS.MANUTENCAO.REMOVIDOS', { n: data.removidos }));
    selecionados.value = new Set();
    await carrega({ silencioso: true });
  } catch (error) {
    useAlert(erroDaApi(error, t('NEUXS_NEXUS.MANUTENCAO.ERRO_ACAO')));
  } finally {
    agindo.value = '';
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <p class="text-sm text-n-slate-11">
      {{ t('NEUXS_NEXUS.MANUTENCAO.EXPLICA') }}
    </p>

    <div v-if="carregando" class="flex justify-center py-10 text-n-slate-11">
      <Spinner />
    </div>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>

    <template v-else>
      <section class="grid gap-4 md:grid-cols-2">
        <div class="flex flex-col gap-2">
          <TextArea
            v-model="texto"
            :label="t('NEUXS_NEXUS.MANUTENCAO.MENSAGEM')"
            :max-length="dados.mensagem.tamanho_maximo || 1000"
            show-character-count
            auto-height
            min-height="8rem"
          />
          <p class="text-xs text-n-slate-10">
            {{ t('NEUXS_NEXUS.MANUTENCAO.MENSAGEM_DICA') }}
          </p>
          <div>
            <Button
              :label="t('NEUXS_NEXUS.MANUTENCAO.SALVAR')"
              size="sm"
              :disabled="!mudouTexto || !texto.trim()"
              :is-loading="salvando"
              @click="salva"
            />
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('NEUXS_NEXUS.MANUTENCAO.PREVIA') }}
          </span>
          <p
            class="p-3 text-sm whitespace-pre-line rounded-xl bg-n-teal-3 text-n-slate-12 min-h-[8rem]"
          >
            {{ previa || '—' }}
          </p>
        </div>
      </section>

      <section class="flex flex-wrap items-center gap-2">
        <Button
          :label="t('NEUXS_NEXUS.MANUTENCAO.ENVIAR_TODOS', { n: liberados })"
          icon="i-lucide-send"
          size="sm"
          :disabled="!liberados || mudouTexto || semMensagem"
          :is-loading="agindo === 'todos'"
          @click="envia(null)"
        />
        <Button
          v-if="selecionados.size"
          :label="
            t('NEUXS_NEXUS.MANUTENCAO.ENVIAR_SELECIONADOS', {
              n: selecionados.size,
            })
          "
          size="sm"
          variant="faded"
          :disabled="mudouTexto || semMensagem"
          :is-loading="agindo === 'selecionados'"
          @click="envia([...selecionados])"
        />
        <Button
          v-if="selecionados.size"
          :label="
            t('NEUXS_NEXUS.MANUTENCAO.REMOVER_SELECIONADOS', {
              n: selecionados.size,
            })
          "
          size="sm"
          variant="ghost"
          color="ruby"
          :is-loading="agindo === 'remover'"
          @click="remove"
        />
        <span class="text-xs text-n-slate-10">
          {{
            t('NEUXS_NEXUS.MANUTENCAO.LIMITE', { n: dados.limite_por_envio })
          }}
        </span>
      </section>
      <p
        v-if="esperando > 0"
        class="p-3 text-sm rounded-xl bg-n-amber-3 text-n-amber-11"
      >
        {{ t('NEUXS_NEXUS.MANUTENCAO.ESPERANDO', { n: esperando }) }}
      </p>

      <section class="flex flex-wrap gap-1">
        <Button
          :label="t('NEUXS_NEXUS.MANUTENCAO.TODOS')"
          size="xs"
          :variant="filtro === '' ? 'solid' : 'ghost'"
          color="slate"
          @click="filtro = ''"
        />
        <Button
          v-for="estado in ESTADOS"
          :key="estado"
          :label="`${t(`NEUXS_NEXUS.MANUTENCAO.ESTADOS.${estado}`)} · ${dados.resumo[estado] || 0}`"
          size="xs"
          :variant="filtro === estado ? 'solid' : 'ghost'"
          color="slate"
          @click="filtro = estado"
        />
      </section>

      <p v-if="!dados.contatos.length" class="text-sm text-n-slate-11">
        {{ t('NEUXS_NEXUS.MANUTENCAO.VAZIO') }}
      </p>
      <ul v-else class="flex flex-col divide-y divide-n-weak">
        <li
          v-for="contato in dados.contatos"
          :key="contato.id"
          class="flex items-start gap-3 py-3"
        >
          <input
            type="checkbox"
            class="mt-1"
            :disabled="!selecionavel(contato)"
            :checked="selecionados.has(contato.id)"
            @change="alterna(contato.id)"
          />
          <div class="flex flex-col flex-1 min-w-0 gap-0.5">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ contato.nome || contato.telefone || '—' }}
              </span>
              <span v-if="contato.nome" class="text-xs text-n-slate-10">
                {{ contato.telefone }}
              </span>
              <span
                class="px-2 text-xs rounded-full"
                :class="COR[contato.estado]"
              >
                {{ t(`NEUXS_NEXUS.MANUTENCAO.ESTADOS.${contato.estado}`) }}
              </span>
              <span
                v-if="contato.liberado === false"
                class="px-2 text-xs rounded-full bg-n-amber-3 text-n-amber-11"
              >
                {{ t('NEUXS_NEXUS.MANUTENCAO.EM_ANDAMENTO') }}
              </span>
              <span
                v-else-if="contato.manutencao && !contato.manutencao.conferida"
                class="text-xs text-n-slate-10"
              >
                {{ t('NEUXS_NEXUS.MANUTENCAO.NAO_CONFERIDA') }}
              </span>
              <span v-if="contato.entrega" class="text-xs text-n-slate-11">
                {{ t(`NEUXS_NEXUS.MANUTENCAO.ENTREGA.${contato.entrega}`) }}
              </span>
            </div>
            <div class="flex flex-wrap gap-x-3 text-xs text-n-slate-10">
              <router-link
                :to="`/app/accounts/${accountId}/conversations/${contato.conversa_id}`"
                class="underline"
              >
                #{{ contato.conversa_id }}
              </router-link>
              <span v-if="contato.manutencao && contato.manutencao.descricao">
                {{ contato.manutencao.descricao }}
              </span>
              <span v-if="contato.conversas > 1">
                {{
                  t('NEUXS_NEXUS.MANUTENCAO.CONVERSAS', {
                    n: contato.conversas,
                  })
                }}
              </span>
              <span>
                {{
                  t('NEUXS_NEXUS.MANUTENCAO.ULTIMO_CONTATO', {
                    quando: quando(contato.ultimo_contato_em),
                  })
                }}
              </span>
              <template v-if="contato.estado === 'aguardando'">
                <span v-if="janelaAberta(contato.janela_fecha_em)">
                  {{
                    t('NEUXS_NEXUS.MANUTENCAO.JANELA_FECHA', {
                      quando: quando(contato.janela_fecha_em),
                    })
                  }}
                </span>
                <span v-else class="text-n-ruby-11">
                  {{ t('NEUXS_NEXUS.MANUTENCAO.JANELA_FECHADA') }}
                </span>
              </template>
              <span v-if="contato.enviado_em">
                {{
                  t('NEUXS_NEXUS.MANUTENCAO.ENVIADO_EM', {
                    quando: quando(contato.enviado_em),
                  })
                }}
              </span>
            </div>
            <p
              v-if="contato.erro || contato.erro_da_meta"
              class="text-xs text-n-ruby-11"
            >
              {{ contato.erro || contato.erro_da_meta }}
            </p>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>
